import React, { useState } from "react";
import { GraduationCap, Shield, Users, BookOpen, HandHeart, MessageCircle, Heart, Stethoscope, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "@/components/site/SectionHeader";
import { submitContact } from "@/lib/api";

const SERVICES = [
  { icon: GraduationCap, title: "School-based sexuality education", text: "Hauora-aligned, age-appropriate sessions designed for Aotearoa classrooms." },
  { icon: Shield, title: "Consent & healthy relationships workshops", text: "Practical modules on consent, boundaries, communication and digital safety." },
  { icon: Heart, title: "Youth wellbeing education", text: "Connecting sexual health with mental, emotional and whānau wellbeing." },
  { icon: HandHeart, title: "Community outreach initiatives", text: "Engagement at marae, churches, sports clubs and community hubs across Auckland." },
  { icon: BookOpen, title: "Sexual health literacy", text: "Plain-language resources on bodies, relationships and informed decision-making." },
  { icon: Stethoscope, title: "STI awareness & prevention", text: "Reducing stigma and improving understanding of testing, prevention and care." },
  { icon: Users, title: "Peer education development", text: "Building pathways for rangatahi to lead the kōrero with their peers." },
  { icon: MessageCircle, title: "Resource navigation & support", text: "Connecting young people and whānau with the right clinical and social services." },
];

export default function Programmes() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", message: "", subject: "Programme enquiry" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Enquiry sent — we'll be in touch.");
      setForm({ name: "", email: "", organisation: "", message: "", subject: "Programme enquiry" });
    } catch {
      toast.error("Couldn't send enquiry. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <>
      <section className="container-x pt-20 pb-12 sm:pt-28">
        <div className="max-w-4xl">
          <span className="label-eyebrow">— Our Programme for Rangatahi</span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-heading font-semibold tracking-tighter leading-[1.02] text-balance">
            Sexual health &amp; wellbeing education, designed with rangatahi.
          </h1>
          <p className="mt-8 text-lg text-ink/65 max-w-2xl leading-relaxed">
            Our programme delivers evidence-informed, age-appropriate and culturally responsive education that helps
            rangatahi build knowledge, confidence and skills around their health, relationships and wellbeing.
            Complementary workshops for schools, organisations and professionals are available via our{" "}
            <a href="/workshops" className="text-crimson underline-offset-4 hover:underline">Workshops page</a>.
          </p>
        </div>
      </section>

      <section className="section-y !pt-12">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map(({ icon: Icon, title, text }, i) => (
              <article key={title} className="card-surface group" data-testid={`service-card-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-sand group-hover:bg-crimson transition-colors flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-ink group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-ink leading-snug">{title}</h3>
                <p className="mt-3 text-ink/65 text-sm leading-relaxed">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand">
        <div className="container-x py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionHeader eyebrow="Programme enquiry" title="Talk to us about working together." description="If you'd like to learn more, explore collaboration, or invite us to your school or community — we'd love to kōrero." />
              <div className="mt-8 p-6 rounded-2xl bg-white border border-stone">
                <p className="text-sm text-ink/65 leading-relaxed">
                  Our programmes are in active development. We respond to all enquiries personally and welcome conversations
                  with educators, community workers, iwi, and youth-serving organisations.
                </p>
              </div>
            </div>

            <form onSubmit={onSubmit} className="lg:col-span-7 bg-white rounded-3xl p-8 sm:p-10 border border-stone" data-testid="programme-enquiry-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Your name</label>
                  <input required className="input-field" value={form.name} onChange={onChange("name")} data-testid="enquiry-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Email</label>
                  <input required type="email" className="input-field" value={form.email} onChange={onChange("email")} data-testid="enquiry-email" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-ink/80 mb-2 block">School / organisation</label>
                  <input className="input-field" value={form.organisation} onChange={onChange("organisation")} data-testid="enquiry-organisation" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Tell us about your community or kaupapa</label>
                  <textarea required rows={5} className="input-field resize-none" value={form.message} onChange={onChange("message")} data-testid="enquiry-message" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="mt-7 btn-primary" data-testid="enquiry-submit">
                {loading ? "Sending..." : <>Send enquiry <ArrowUpRight className="w-4 h-4" /></>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
